export type BubblePosition = "bottom-right" | "bottom-left"
export type ResponseStyle = "formal" | "friendly" | "casual"
export type EscalationAction = "collect-email" | "show-contact" | "live-chat"
export type ConversionGoal = "appointment" | "callback" | "answer-only"
export type CalendarKind = "" | "cal_com" | "calendly"

// The flat slice of agent.setup_data the Website Chatbot wizard owns (IMP-332).
// Knowledge is NOT here — it's the shared account knowledge base, managed via
// its own endpoints in the Knowledge step.
export interface ChatbotSetupData {
  bot_name: string
  primary_color: string
  bubble_position: BubblePosition
  welcome_message: string
  // IMP-454: whether the pre-open popup (the proactive teaser bubble above the
  // launcher) shows. It reuses the welcome message copy — there's no separate
  // popup text.
  popup_enabled: boolean
  conversation_starters: string[]
  response_style?: ResponseStyle
  // IMP-463: the two independent capabilities. These replace the exclusive
  // conversion_goal + escalation_action model (kept optional below so agents
  // saved before the split still type-check); escalation is derived — either
  // capability on means a stuck bot collects details, both off means it
  // shares the business's contact info.
  collect_enabled: boolean
  booking_enabled: boolean
  escalation_action?: EscalationAction
  conversion_goal?: ConversionGoal
  // Appointment goal config — reuses the receptionist's keys so the booking
  // flow (IMP-337) can share its infrastructure.
  appointment_calendar_kind: CalendarKind
  appointment_event_type_uri: string
  // The domain where the widget is embedded (e.g. "yoursite.com"); captured on
  // the install step and verified server-side so the widget only runs there.
  embed_domain: string
  // IMP-373: optional free-text the business adds to tailor the bot's behavior;
  // populates a dedicated custom-instructions section of the prompt.
  custom_instructions: string
}

// IMP-463: read the two capability facts off saved setup_data, deriving them
// from the legacy goal/escalation keys for agents saved before the split.
// Mirrors Agents::WebsiteChat::Capabilities server-side — keep in sync.
export function deriveCollect(saved: Partial<ChatbotSetupData>): boolean {
  if (typeof saved.collect_enabled === "boolean") return saved.collect_enabled
  if (!saved.conversion_goal) return true // fresh agent: collect by default
  return (
    saved.conversion_goal !== "answer-only" ||
    saved.escalation_action === "collect-email"
  )
}

export function deriveBooking(saved: Partial<ChatbotSetupData>): boolean {
  if (typeof saved.booking_enabled === "boolean") return saved.booking_enabled
  return saved.conversion_goal === "appointment"
}

export interface ChatbotSetupStepProps {
  stepNumber: number
  totalSteps: number
  data: ChatbotSetupData
  onUpdate: (patch: Partial<ChatbotSetupData>) => void
  onNext: () => void
  onBack: () => void
  // The last step's primary button reads "Launch chatbot" instead of "Continue".
  nextLabel?: string
  processing?: boolean
}
